import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';

import { styled, Checkbox } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import Link from '@material-ui/core/Link';
import Box from '@material-ui/core/Box';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableContainer from '@material-ui/core/TableContainer';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import TablePagination from '@material-ui/core/TablePagination';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';

import { useAPI } from '../../utils/api';
import { makeStyles } from '@material-ui/styles';

const useStyles = makeStyles({
  root: {
    '&:last-of-type td': {
      borderBottom: 'none',
    },
  },
});

const MemberItemContainer = styled(Grid)({
  textAlign: 'center',
  borderLeft: '1px solid',
});

const MemberItemGrid = styled(Grid)({
  padding: 6,
  borderBottom: '1px solid',
  borderRight: '1px solid',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflowWrap: 'anywhere',
});

const ParsedCell = ({ text, isStatus }) => {
  const data = text ? text.split('\n').map((s) => s.trim()) : [];
  return (
    <>
      {data.map((string, index) =>
        isStatus && index === 0 ? (
          <Box key={`${string}-${index}`}>
            <Link href={`/#/record/status/${string}`}>{string}</Link>
          </Box>
        ) : (
          <Box key={`${string}-${index}`}>{string}</Box>
        )
      )}
    </>
  );
};

ParsedCell.propTypes = {
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isStatus: PropTypes.bool,
};

ParsedCell.defaultProps = {
  text: '',
  isStatus: false,
};

const columns = [
  { id: 'orderStatus', label: '案件狀態' },
  { id: 'memberInfo', label: '會員資訊' },
  {
    id: 'sender',
    label: '取件方',
  },
  {
    id: 'receiver',
    label: '送件方',
  },
  {
    id: 'orderInfo',
    label: '案件資訊',
  },
];

const PreorderRecordPage = () => {
  const classes = useStyles();
  const { memberID } = useParams();
  const api = useAPI();

  const listForm = useRef();

  const [shouldPageUpdated, setShouldPageUpdated] = useState(false);

  const [orders, setOrders] = useState([]);
  const [isUnmounted, setIsUnmounted] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    return () => {
      setShouldPageUpdated(false);
    };
  }, [shouldPageUpdated]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  useEffect(() => {
    api
      .getPreOrderList(memberID)
      .then((orderData) => {
        if (!isUnmounted) {
          setOrders(orderData);
        }
      })
      .catch(() => {});

    return () => {
      setIsUnmounted(true);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Box ref={listForm} component="form">
        <Paper
          sx={{ width: '100%', overflow: 'hidden', padding: 1 }}
          elevation={5}
        >
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow
                  sx={{
                    display: { xs: 'none', sm: 'table-row' },
                  }}
                >
                  {orders && columns
                    ? columns.map((column, index) => (
                        <TableCell
                          key={`${column.id}-${index}`}
                          align={column.align || 'center'}
                          sx={{
                            minWidth: column.minWidth || 'none',
                            padding: 1,
                            maxWidth: '100%',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {column.label}
                        </TableCell>
                      ))
                    : undefined}
                </TableRow>
              </TableHead>

              <TableBody>
                {orders && !shouldPageUpdated
                  ? orders
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      .map((row, rowIndex) => {
                        return (
                          <TableRow
                            hover
                            role="checkbox"
                            tabIndex={-1}
                            key={`${row.memberID}-${rowIndex}`}
                            className={classes.root}
                          >
                            {orders && columns
                              ? columns.map((column, columnIndex) => {
                                  return (
                                    <TableCell
                                      key={`${column.id}-${columnIndex}`}
                                      align={column.align || 'center'}
                                      sx={{
                                        display: {
                                          xs: 'none',
                                          sm: 'table-cell',
                                        },
                                        padding: 1,
                                        minHeight: '0',
                                        maxWidth: '100%',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      <ParsedCell
                                        text={row[column.id]}
                                        isStatus={column.id === 'orderStatus'}
                                      />
                                    </TableCell>
                                  );
                                })
                              : undefined}

                            <TableCell
                              sx={{
                                display: {
                                  xs: 'block',
                                  sm: 'none',
                                  width: '100%',
                                },
                              }}
                            >
                              <MemberItemContainer
                                container
                                borderTop="1px solid"
                                fontWeight="bold"
                              >
                                <MemberItemGrid item xs={12}>
                                  案件狀態
                                </MemberItemGrid>
                              </MemberItemContainer>
                              <MemberItemContainer container>
                                <MemberItemGrid item xs={12}>
                                  <Checkbox
                                    name={`readyToPrint-${rowIndex}`}
                                    value="true"
                                    color="primary"
                                  />
                                </MemberItemGrid>
                              </MemberItemContainer>
                              <MemberItemContainer
                                container
                                borderTop="1px solid"
                                fontWeight="bold"
                              >
                                <MemberItemGrid item xs={12}>
                                  案件狀態
                                </MemberItemGrid>
                              </MemberItemContainer>
                              <MemberItemContainer container>
                                <MemberItemGrid item xs={12}>
                                  {row.orderStatus}
                                </MemberItemGrid>
                              </MemberItemContainer>
                              <MemberItemContainer container fontWeight="bold">
                                <MemberItemGrid item xs={12}>
                                  會員資訊
                                </MemberItemGrid>
                              </MemberItemContainer>
                              <MemberItemContainer container>
                                <MemberItemGrid item xs={12}>
                                  {row.memberInfo}
                                </MemberItemGrid>
                              </MemberItemContainer>
                              <MemberItemContainer container fontWeight="bold">
                                <MemberItemGrid item xs={12}>
                                  取件方
                                </MemberItemGrid>
                              </MemberItemContainer>
                              <MemberItemContainer container>
                                <MemberItemGrid item xs={12}>
                                  {row.receiver}
                                </MemberItemGrid>
                              </MemberItemContainer>
                              <MemberItemContainer container fontWeight="bold">
                                <MemberItemGrid item xs={12}>
                                  送件方
                                </MemberItemGrid>
                              </MemberItemContainer>
                              <MemberItemContainer container>
                                <MemberItemGrid item xs={12}>
                                  {row.sender}
                                </MemberItemGrid>
                              </MemberItemContainer>
                              <MemberItemContainer container fontWeight="bold">
                                <MemberItemGrid item xs={12}>
                                  案件資訊
                                </MemberItemGrid>
                              </MemberItemContainer>
                              <MemberItemContainer container>
                                <MemberItemGrid item xs={12}>
                                  {row.orderInfo}
                                </MemberItemGrid>
                              </MemberItemContainer>
                            </TableCell>
                          </TableRow>
                        );
                      })
                  : undefined}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            labelRowsPerPage="每頁顯示"
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={orders ? orders.length : 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </Box>
    </>
  );
};

export default PreorderRecordPage;
